import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, EyeOff, X, Save, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN } from '@/constants/testIds';
import ImageUpload from '@/components/ImageUpload';

const API = '/api';
function getHeaders() {
  const t = localStorage.getItem('pkepl_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const EMPTY_FORM = { title: '', slug: '', summary: '', content: '', category: 'General', tags: '', author: 'NKP VENTURES Editorial Team', status: 'draft', seo_title: '', seo_description: '', featured_image: null };
const CATEGORIES = ['General', 'Business Strategy', 'Operations', 'Customer Support', 'Healthcare', 'Industry Insights'];

function generateSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
}

function formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; }

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchPosts = () => {
    setLoading(true);
    axios.get(`${API}/admin/blog`, { withCredentials: true, headers: getHeaders() })
      .then(r => {
        const data = r.data;
        setPosts(Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []));
      }).catch(() => toast.error('Failed to load posts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditId(null); setModalOpen(true); };
  const openEdit = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/blog/${id}`, { withCredentials: true, headers: getHeaders() });
      setForm({ ...data, tags: Array.isArray(data.tags) ? data.tags.join(', ') : (data.tags || '') });
      setEditId(id); setModalOpen(true);
    } catch { toast.error('Failed to load post'); }
  };

  const handleSave = async () => {
    if (!form.title || !form.slug || !form.summary || !form.content) { toast.error('Title, slug, summary, and content are required.'); return; }
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    try {
      if (editId) {
        await axios.put(`${API}/admin/blog/${editId}`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Post created');
      }
      setModalOpen(false); fetchPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Post deleted'); setDeleteId(null); fetchPosts();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleStatus = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { status: newStatus }, { withCredentials: true, headers: getHeaders() });
      toast.success(`Post ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      fetchPosts();
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Blog & Insights</h1>
          <p className="text-sm text-[#6B7280] font-sans mt-1">Manage your published articles and drafts.</p>
        </div>
        <button data-testid={ADMIN.blogNewBtn} onClick={openNew}
          className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors duration-200">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid={ADMIN.blogList}>
        {loading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280] font-sans">No posts yet. Create your first post.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full admin-table">
              <thead className="bg-[#F9FAFB]">
                <tr><th className="text-left">Title</th><th className="text-left hidden md:table-cell">Category</th><th className="text-left">Status</th><th className="text-left hidden sm:table-cell">Date</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post.id}>
                    <td>
                      <p className="font-medium text-[#1F2937] text-sm line-clamp-1">{post.title}</p>
                      <p className="text-xs text-[#9CA3AF]">/insights/{post.slug}</p>
                    </td>
                    <td className="hidden md:table-cell text-[#6B7280] text-sm">{post.category || '—'}</td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${post.status === 'published' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell text-[#9CA3AF] text-sm">{formatDate(post.created_at)}</td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => toggleStatus(post)} title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                          className="p-1.5 text-[#9CA3AF] hover:text-[#0E5A57] rounded-lg hover:bg-teal-50 transition-colors duration-150">
                          {post.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => openEdit(post.id)}
                          className="p-1.5 text-[#9CA3AF] hover:text-[#0E5A57] rounded-lg hover:bg-teal-50 transition-colors duration-150">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(post.id)}
                          className="p-1.5 text-[#9CA3AF] hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors duration-150">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
              <h2 className="font-serif text-xl font-bold text-[#153E5C]">{editId ? 'Edit Post' : 'New Post'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Title *</label>
                  <input value={form.title} onChange={e => { setForm(p => ({ ...p, title: e.target.value, slug: generateSlug(e.target.value) })); }}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="Article title" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Slug *</label>
                  <input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="url-slug" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Category</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans bg-white focus:outline-none focus:border-[#0E5A57]">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans bg-white focus:outline-none focus:border-[#0E5A57]">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Tags (comma separated)</label>
                  <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="BPO, operations, strategy" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Summary *</label>
                  <textarea value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} rows={2}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans resize-none focus:outline-none focus:border-[#0E5A57]" placeholder="Brief summary for listing pages" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Content (HTML) *</label>
                  <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={10}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-mono resize-y focus:outline-none focus:border-[#0E5A57]" placeholder="<h2>Section</h2><p>Content...</p>" />
                </div>
                <div className="sm:col-span-2">
                  <ImageUpload
                    label="Featured Image (Optional)"
                    value={form.featured_image}
                    previewUrl={form.featured_image ? `/api/media/${form.featured_image}` : null}
                    onChange={(id) => setForm(p => ({ ...p, featured_image: id }))}
                    onClear={() => setForm(p => ({ ...p, featured_image: null }))}
                    hint="Blog post cover image. JPG, PNG or WEBP · Max 5MB"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">SEO Title</label>
                  <input value={form.seo_title} onChange={e => setForm(p => ({ ...p, seo_title: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="SEO page title" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">SEO Description</label>
                  <input value={form.seo_description} onChange={e => setForm(p => ({ ...p, seo_description: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="Meta description" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E5E7EB]">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm font-medium text-[#6B7280] hover:text-[#374151] transition-colors duration-150 font-sans">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors duration-200">
                {saving ? <><Loader className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> {editId ? 'Update' : 'Create'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-xl text-center">
            <p className="font-serif text-xl font-bold text-[#153E5C] mb-3">Delete Post?</p>
            <p className="text-sm text-[#6B7280] mb-6 font-sans">This action cannot be undone.</p>
            <div className="flex items-center gap-3 justify-center">
              <button onClick={() => setDeleteId(null)} className="px-5 py-2.5 text-sm font-medium text-[#6B7280] border border-[#E5E7EB] rounded-xl hover:bg-[#F9FAFB] transition-colors duration-150 font-sans">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="px-5 py-2.5 text-sm font-medium bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors duration-150 font-sans">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
